import React from "react";
import { AlertTriangle, BadgeCheck, Clock, FileText, Pencil, PenLine } from "lucide-react";
import { formatCalendarDate } from "../../lib/calendar-date";
import { parseMoney } from "../../lib/display-labels";

/**
 * Rental field tickets: the ticket shape, its mappers and its status skin,
 * shared by the register (/rentals/tickets) and the ticket page
 * (/rentals/tickets/:id). Moved out of RentalFieldTickets.tsx unchanged when
 * the split list / detail pane became two pages (#815).
 */

/**
 * A rental field ticket as GET /surface-rentals/tickets sends it: `t.*` plus
 * `created_by_name` and `line_item_count`. The detail route adds `line_items`.
 *
 * THIS PAGE READ A CAMELCASE TICKET THE API HAS NEVER SENT (#803): `wellName`,
 * `customer`, `poNumber`, `totalDays`, `dailyRate`, `lineItems`, `date`. The
 * search filter called `ticket.wellName.toLowerCase()`, so the first real
 * ticket would have thrown and taken the page down. Every field below is the
 * column's own name, and every one may be null.
 */
export interface LineItem {
  id?: string;
  description: string | null;
  asset_id: string | null;
  asset_name: string | null;
  asset_number: string | null;
  /** NUMERIC columns arrive as strings; parsed, and null when absent. */
  days: number | null;
  rate: number | null;
  amount: number | null;
}

export interface FieldTicket {
  id: string;
  ticket_number: string | null;
  customer_name: string | null;
  well_name: string | null;
  po_number: string | null;
  status: string | null;
  ticket_type: string | null;
  period_start: string | null;
  period_end: string | null;
  total_days: number | null;
  daily_rate: number | null;
  subtotal: number | null;
  gst_amount: number | null;
  /** The TOTAL tax the ticket stores (api#1019); `tax_type` names it ("GST + PST"). */
  tax_amount: number | null;
  tax_type: string | null;
  total: number | null;
  line_item_count: number | null;
  created_at: string | null;
  supervisor_signed: boolean | null;
  customer_signed: boolean | null;
  /** Present only once the detail route has answered. */
  line_items?: LineItem[];
}

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));
const bool = (v: unknown): boolean | null => (v === true ? true : v === false ? false : null);

export function toLineItem(r: Record<string, unknown>): LineItem {
  return {
    id: str(r.id) ?? undefined,
    description: str(r.description),
    asset_id: str(r.asset_id),
    asset_name: str(r.asset_name),
    asset_number: str(r.asset_number),
    days: parseMoney(r.days),
    rate: parseMoney(r.rate),
    amount: parseMoney(r.amount),
  };
}

export function toTicket(r: Record<string, unknown>): FieldTicket {
  return {
    id: String(r.id ?? ""),
    ticket_number: str(r.ticket_number),
    customer_name: str(r.customer_name),
    well_name: str(r.well_name),
    po_number: str(r.po_number),
    status: str(r.status),
    ticket_type: str(r.ticket_type),
    period_start: str(r.period_start),
    period_end: str(r.period_end),
    total_days: parseMoney(r.total_days),
    daily_rate: parseMoney(r.daily_rate),
    subtotal: parseMoney(r.subtotal),
    gst_amount: parseMoney(r.gst_amount),
    tax_amount: parseMoney(r.tax_amount),
    tax_type: str(r.tax_type),
    total: parseMoney(r.total),
    line_item_count: parseMoney(r.line_item_count),
    created_at: str(r.created_at),
    supervisor_signed: bool(r.supervisor_signed),
    customer_signed: bool(r.customer_signed),
    line_items: Array.isArray(r.line_items) ? (r.line_items as Record<string, unknown>[]).map(toLineItem) : undefined,
  };
}

/**
 * The rental period, from the two calendar-day columns the ticket stores. It
 * was a fixed "Apr 4 - Apr 12, 2026" on every ticket (#803). Either end
 * missing renders as a dash, never a guessed date.
 */
export function periodLabel(t: FieldTicket): string {
  const fmt = (d: string | null) => formatCalendarDate(d, { month: "short", day: "numeric", year: "numeric" }) ?? "—";
  if (!t.period_start && !t.period_end) return "—";
  return `${fmt(t.period_start)} – ${fmt(t.period_end)}`;
}

/**
 * The sign-off boxes say only what a ticket records: whether each party signed.
 * They used to name a made-up signer on every ticket ("John Davidson", "Mike
 * Richards") and print a fixed date and time under the tick (#803). A rentals
 * ticket stores two booleans and nothing else, no signer, no time and no image,
 * so no name or date is shown.
 */
export function signedFlag(flag: boolean | null): boolean {
  return flag === true;
}

export type Skin = { label: string; fill: string; rail: string };

/**
 * Exhaustive over `rental_field_tickets_status_check`. Six statuses, five hues:
 * Approved and Invoiced share green (both settled, the label tells them apart),
 * and Draft is slate (nothing has happened to it yet). The old map knew four of
 * the six, so a `draft` ticket — which is what every POST /tickets creates —
 * crashed the list on `status.icon`. An unknown value is a slate dash with a
 * transparent rail.
 */
export const STATUS: Record<string, Skin & { icon: typeof Clock }> = {
  draft: { label: "Draft", fill: "bg-fi-signal-slate", rail: "border-l-fi-steel", icon: Pencil },
  pending: { label: "Pending", fill: "bg-fi-signal-amber", rail: "border-l-fi-amber", icon: Clock },
  signed: { label: "Signed", fill: "bg-fi-signal-blue", rail: "border-l-fi-blue", icon: PenLine },
  approved: { label: "Approved", fill: "bg-fi-signal-green", rail: "border-l-fi-green", icon: BadgeCheck },
  invoiced: { label: "Invoiced", fill: "bg-fi-signal-green", rail: "border-l-fi-green", icon: FileText },
  rejected: { label: "Rejected", fill: "bg-fi-signal-red", rail: "border-l-fi-red", icon: AlertTriangle },
};
const UNKNOWN: Skin = { label: "—", fill: "bg-fi-signal-slate", rail: "border-l-transparent" };
export const skinOf = (s: string | null): Skin =>
  s != null && Object.prototype.hasOwnProperty.call(STATUS, s) ? STATUS[s] : UNKNOWN;

/** Exhaustive over `rental_field_tickets_ticket_type_check`. An outline chip: a kind, not a state. */
export const TICKET_TYPE: Record<string, string> = {
  delivery: "Delivery",
  return: "Return",
  field_service: "Field Service",
  standby: "Standby",
};

export function StatusBadge({ status }: { status: string | null }) {
  const skin = skinOf(status);
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

export function TypeChip({ type }: { type: string | null }) {
  if (!type) return null;
  return (
    <span className="inline-flex whitespace-nowrap rounded-fi-pill border border-fieldiq-border px-2.5 py-1 text-[0.68rem] font-bold uppercase leading-none tracking-[0.06em] text-fieldiq-text">
      {TICKET_TYPE[type] ?? "—"}
    </span>
  );
}

/**
 * The register asks GET /tickets for this many rows, the API's maximum
 * (`Math.min(limit, 500)`). Below it the list is complete and a tally of it is
 * the real count; at it there may be more, so every count is withheld.
 */
export const LIST_CAP = 500;
